import { FC, useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Button from '@/storybook/components/Button/Button';
import Drawer from '@/storybook/components/Drawer/Drawer';
import LoadingSpinner from '@/storybook/components/LoadingSpinner/LoadingSpinner';
import Table from '@/storybook/components/Table/Table';
import { UserFilterListItem } from '@/models/user/userFilter';
import { SearchIcon } from '@/storybook/icons';
import { AppliedFilterSetStorage } from '@/utils/filter/appliedFilterSetStorage';
import useManageFiltersColumns from './useManageFiltersColumns';
import useManageFiltersActions from './useManageFiltersActions';
import FilterDetailsDialog from './FilterDetailsDialog';
import RenameFilterDialog from './RenameFilterDialog';
import DeleteFilterDialog from './DeleteFilterDialog';
import SharedViewDialog from '@/components/filter/SharedViewDialog/SharedViewDialog';

import { PAGINATION_OPTIONS } from '@/utils/tableUtils';
import overlayStyles from '@/styles/shared/overlay.module.css';
import styles from './ManageSavedFiltersDrawer.module.css';

interface ManageSavedFiltersDrawerProps {
  isOpen: boolean;
  filters: UserFilterListItem[];
  onClose: () => void;
  onFiltersChanged: () => void;
}

const ManageSavedFiltersDrawer: FC<ManageSavedFiltersDrawerProps> = ({
  isOpen,
  filters,
  onClose,
  onFiltersChanged,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilterId, setSelectedFilterId] = useState<string | null>(null);
  const [hasDraft, setHasDraft] = useState(false);
  const appliedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    const appliedSet = AppliedFilterSetStorage.get();
    if (appliedSet) {
      setSelectedFilterId(appliedSet.userFilterId);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (selectedFilterId && !appliedRef.current) {
      setHasDraft(true);
    }
    appliedRef.current = false;
    onClose();
  }, [selectedFilterId, onClose]);

  const actions = useManageFiltersActions(onFiltersChanged, handleClose, filters);

  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return filters;
    const query = searchQuery.toLowerCase();
    return filters.filter(savedFilter => {
      const name = (savedFilter.filterName ?? savedFilter.userFilterId).toLowerCase();
      return name.includes(query);
    });
  }, [filters, searchQuery]);

  const columns = useManageFiltersColumns({
    selectedFilterId,
    onSelect: setSelectedFilterId,
    onSeeDetails: actions.handleSeeDetails,
    onRename: actions.handleRename,
    onDelete: actions.handleDelete,
    onSetAsDefault: actions.handleSetAsDefault,
    onDeselectDefault: actions.handleDeselectDefault,
    onShareFilter: actions.handleShareFilter,
  });

  const handleApply = useCallback(() => {
    if (!selectedFilterId) return;
    appliedRef.current = true;
    setHasDraft(false);
    actions.handleApplyFilters(selectedFilterId);
  }, [selectedFilterId, actions]);

  const applyVariant = selectedFilterId && hasDraft ? 'pending' : 'primary';

  const footerContent = (
    <div className={styles.footerRight}>
      <Button size="L" variant="text" color="default" onClick={handleClose}>
        {t('global.filters.manageSavedFilters.cancel')}
      </Button>
      <Button variant={applyVariant} disabled={!selectedFilterId} onClick={handleApply}>
        {t('global.filters.manageSavedFilters.applySet')}
      </Button>
    </div>
  );

  return (
    <>
      {(actions.isApplying || actions.isSettingDefault) &&
        createPortal(
          <div className={overlayStyles.fullPageLoader} data-testid="manage-saved-filters-loader">
            <LoadingSpinner />
          </div>,
          document.body,
        )}

      <Drawer
        isOpen={isOpen}
        onClose={handleClose}
        title={t('global.filters.manageSavedFilters.title')}
        position="top"
        hideButtonLabel={t('global.filters.manageSavedFilters.hideButton')}
        footerContent={footerContent}
        data-testid="manage-saved-filters-drawer"
        className={styles.drawer}
        contentClassName={styles.drawerContent}
      >
        <div className={styles.searchWrapper}>
          <div className={styles.searchInputWrapper}>
            <SearchIcon className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder={String(t('global.filters.manageSavedFilters.searchPlaceholder'))}
              aria-label={String(t('global.filters.manageSavedFilters.searchPlaceholder'))}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              data-testid="manage-saved-filters-search"
            />
          </div>
        </div>
        <div>
          <Table
            className={styles.tableWrapper}
            columns={columns}
            data={filteredData}
            data-testid="manage-saved-filters-table"
            options={{ pageSize: 10, displayPageInfoAtBottom: true, paginationSelectBox: PAGINATION_OPTIONS }}
            initState={{ sort: [{ id: 'lastModified', desc: true }] }}
            enableColumnResizing={false}
          />
        </div>
      </Drawer>

      <FilterDetailsDialog
        isOpen={actions.activeDialog?.type === 'details'}
        filter={actions.activeDialog?.filter ?? null}
        onClose={actions.closeDialog}
      />

      <RenameFilterDialog
        isOpen={actions.activeDialog?.type === 'rename'}
        isLoading={actions.isDialogLoading}
        filter={actions.activeDialog?.filter ?? null}
        onSave={actions.handleRenameSave}
        onCancel={actions.closeDialog}
      />

      <DeleteFilterDialog
        isOpen={actions.activeDialog?.type === 'delete'}
        isLoading={actions.isDialogLoading}
        filter={actions.activeDialog?.filter ?? null}
        onConfirm={actions.handleDeleteConfirm}
        onCancel={actions.closeDialog}
      />

      {actions.validationDialogProps && <SharedViewDialog {...actions.validationDialogProps} />}
    </>
  );
};

export default ManageSavedFiltersDrawer;
